import {
  createStyles,
  makeStyles,
  Theme,
  Tabs,
  Tab,
} from "@material-ui/core";
import React from "react";
import { StyledTabContainer, StyledTableWrapper } from "../common/_components";
import { getIsMobileApp } from '../../utils/mobileApp';
import EasyProjection from "../common/EasyProjection";
import TopStacks from "./TopStacks";
import Stats from "./stats";
import Weather from "./Weather";
import AllProjections from "./AllProjections";
import { SitesResponse, SlateResponse, SportsResponse } from "../common/store/types";
import { connect } from "react-redux";
import { useGetSlates } from "../common/queries/slateQueries";
import { AuthResponse } from "../preContest/store/types";
import { APIState } from "../utils/api/reducer";
import { userSubscribed } from "../utils/authentication";
import { filterSlates } from "../common/helpers/SelectorHelpers";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      '& .MuiDataGrid-cell--editing': {
        backgroundColor: 'transparent',
      },
      "& .MuiTabs-root": {
        "& .Mui-disabled": {
          backgroundColor: "#555555 !important",
          color: "#BEBEBE",
          cursor: "pointer",
        },
      },
      '& .MuiTabs-indicator': {
        backgroundColor: '#7D7D7D'
      },
    },
    tab: {
      borderRadius: "3px 3px 0px 0px",
      fontSize: '12px',
      fontWeight: 400,
      fontFamily: 'Inter',
      minWidth: 'auto',
      paddingLeft: '16px',
      paddingRight: '16px',
    },
    active: {
      fontWeight: 800,
    },
    buttonWrapper: {
      position: "absolute",
      right: 0,
      display: "flex",
      alignItems: "center",
    },
    exportButton: {
      height: 40,
      borderRadius: 3,
      backgroundColor: "#0042C1",
      padding: "10px 16px",
      margin: "4px 0",
      marginLeft: 8,
      "& a": {
        color: "white !important",
        textDecoration: "none",
        display: "flex",
        alignItems: "center",
      },
      "&:hover": {
        color: "white",
        backgroundColor: "#0042C1",
      },
      "& .MuiSvgIcon-root": {
        marginLeft: 4,
        fontSize: "1rem",
      },
    },
    settingsWrapper: {
      borderTop: "1px solid rgba(251,255,57, .3)",
      borderRight: "1px solid rgba(251,255,57, .3)",
      borderLeft: "1px solid rgba(251,255,57, .3)",
      borderTopLeftRadius: "5px",
      borderTopRightRadius: "5px",
      padding: 4,
    },
  })
);

interface OwnProps {
  readonly app?: string;
}

interface ReduxProps {
  readonly selectedSite: SitesResponse;
  readonly selectedSlate: SlateResponse;
  readonly selectedSport: SportsResponse;
  readonly selectedType: string;
  readonly authenticated: APIState<AuthResponse>;
}

type Props = ReduxProps & OwnProps;

const DataTabs = (props: Props) => {
  const {
    selectedSlate,
    selectedSite,
    selectedSport,
    selectedType,
    authenticated,
    app
  } = props;
  const { data } = useGetSlates(app || 'CONTESTSIM', false);
  const slates = data?.slates;
  const filteredSlates = filterSlates(slates || [], selectedSport, selectedSite, selectedType);
  const handleChange = (event: React.ChangeEvent<{}>, newValue: any) => {
    setValue(newValue);
  };

  const classes = useStyles();
  const [value, setValue] = React.useState("projections");
  const isUserSubscribed = userSubscribed(authenticated, selectedSite, selectedSport);

  const isMainSlate = selectedSlate?.name?.toLowerCase().includes('main');
  const isNFLorNHL = selectedSport?.sport === 'NFL' || selectedSport?.sport === 'NHL';
  const showStatsTab = selectedSport?.sport === 'NFL' || selectedSport?.sport === 'NHL' || selectedSport?.sport === 'NBA' || selectedSport?.sport === 'CFB';

  React.useEffect(() => {
    if (!showStatsTab && value === 'stats') {
      setValue('projections');
    }
  }, [showStatsTab, value]);

  const isPGA = selectedSport?.sport === 'PGA';
  const isNBA = selectedSport?.sport === 'NBA';
  const isMLB = selectedSport?.sport === 'MLB';
  const isShowdown = selectedSlate?.name?.toLowerCase().includes('showdown');
  const showTopStacksTab = (isMainSlate && isNFLorNHL) || (isMLB && !isShowdown);
  const showWeatherTab = isPGA;
  const showAllProjectionsTab = isNBA || isMLB;

  React.useEffect(() => {
    if (!showTopStacksTab && value === 'topstacks') {
      setValue('projections');
    }
  }, [showTopStacksTab, value]);

  React.useEffect(() => {
    if (!showStatsTab && value === 'stats') {
      setValue('projections');
    }
  }, [showStatsTab, value]);

  React.useEffect(() => {
    if (!showWeatherTab && value === 'weather') {
      setValue('projections');
    }
  }, [showWeatherTab, value]);

  React.useEffect(() => {
    if (!showAllProjectionsTab && value === 'allprojections') {
      setValue('projections');
    }
  }, [showAllProjectionsTab, value]);

  return (
    <StyledTabContainer className={classes.root}>
      <Tabs
        value={value}
        indicatorColor="primary"
        textColor="inherit"
        onChange={handleChange}
      >
        <Tab
          value="projections"
          label="Projections"
          className={`${classes.tab} ${
            value === "projections" ? classes.active : ""
          }`}
        ></Tab>
        {showTopStacksTab && (
          <Tab
            value="topstacks"
            label="Top Stacks"
            className={`${classes.tab} ${
              value === "topstacks" ? classes.active : ""
            }`}
          ></Tab>
        )}
        {showStatsTab && (
          <Tab
          value="stats"
          label="Stats"
          className={`${classes.tab} ${
            value === "stats" ? classes.active : ""
          }`}
        ></Tab>
        )}
        {showWeatherTab && (
          <Tab
          value="weather"
          label="Weather/Tee Times"
          className={`${classes.tab} ${
            value === "weather" ? classes.active : ""
          }`}
        ></Tab>
        )}
        {showAllProjectionsTab && (
          <Tab
          value="allprojections"
          label="All Projections"
          className={`${classes.tab} ${
            value === "allprojections" ? classes.active : ""
          }`}
        ></Tab>
        )}

      </Tabs>
      <StyledTableWrapper style={getIsMobileApp() ? { maxHeight: 'calc(100vh - 300px)' } : undefined}>
        {value === "projections" && (
          <EasyProjection
            selectedSlate={isUserSubscribed ? selectedSlate : {} as SlateResponse}
            onSetProjections={() => console.log('')}
            disabled={false}
            disableCustomization
            backgroundColor='#00A16'
            simType="dataHub"
            emptyText={filteredSlates.length === 0
              ? `No ${selectedSport?.sport || ''} slates available right now. Check back closer to game time.`
              : "Select a slate to display projections."}/>
        )}
        {value === "topstacks" && (
          <TopStacks
            selectedSport={selectedSport}
            selectedSite={selectedSite}
            selectedSlate={selectedSlate}
            backgroundColor="#00A16"
            emptyText="Select a sport and site to display top stacks."
          />
        )}
        {value === "stats" && (
          <Stats
            selectedSport={selectedSport}
            backgroundColor="#00A16"
            isUserSubscribed={isUserSubscribed ?? false}
          />
        )}
        {value === "weather" && (
          <Weather
            selectedSport={selectedSport}
            backgroundColor="#00A16"
          />
        )}
        {value === "allprojections" && (
          <AllProjections
            selectedSport={selectedSport}
            selectedSite={selectedSite}
            backgroundColor="#00A16"
            isUserSubscribed={isUserSubscribed ?? false}
          />
        )}
      </StyledTableWrapper>
    </StyledTabContainer>
  );
}

const mapDispatchToProps = {

}

const mapStateToProps = (state: any) => {
  const {
    selectedSite,
    selectedSlate,
    selectedSport,
    selectedType,
    authenticated
  } = state.common;
  return {
    selectedSite,
    selectedSlate,
    selectedSport,
    selectedType,
    authenticated
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(DataTabs);